import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Box, Button, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { TextField } from "../ds";
import AttachmentLink from "./AttachmentLink";
import AttachmentUploader, { type UploadedFile } from "./AttachmentUploader";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import { statusCategory, useWorkflowConfig } from "../hooks/useWorkflow";
import { TERMINAL_CATEGORIES } from "../utils/sla";
import { addNote, invalidateSuggestionCaches } from "../api/ess";
import { apiErrorMessage } from "../utils/apiError";
import { formatDateTime } from "../utils/format";
import type { Suggestion } from "../types/ess";

interface Props {
  suggestion: Suggestion;
}

/**
 * Notes thread for an ESS suggestion: an append-only list of notes
 * (author · timestamp · text · attachments) plus a composer. The
 * composer is shown only when /auth/is_authorized allows `add_note` for
 * the caller in the suggestion's current state — so the active reviewer
 * at each stage (HR / WC / SC / HOD), plus admins, can add notes,
 * including after approval.
 *
 * Notes are read straight off the suggestion (the server surfaces
 * `metadata.notes` as a top-level `notes` array), so there's no separate
 * fetch — the detail query already carries them. Used both as a card on
 * the detail page and inside the edit dialog.
 */
export default function SuggestionNotes({ suggestion }: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [text, setText] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);

  // A closed suggestion (implemented-and-closed, or closed as rejected) is
  // a finished record: the thread stays readable but takes no new notes,
  // whatever the add_note probe says.
  const { data: workflow } = useWorkflowConfig();
  const closed = TERMINAL_CATEGORIES.has(
    statusCategory(workflow, suggestion.status),
  );

  const canAdd = useIsAuthorized(
    {
      action: "add_note",
      entity_type: "ess_suggestion",
      entity_id: suggestion.id,
      workflow: "ess_suggestion",
    },
    // `add_note` lives on each active state with a stage-specific role
    // gate, so re-probe whenever the suggestion changes state.
    [suggestion.status],
  );

  const mutation = useMutation({
    mutationFn: () =>
      addNote(suggestion.id, {
        note: text.trim(),
        attachment_ids: files.map((f) => f.asset_id),
      }),
    onSuccess: (fresh) => {
      // Prime the detail cache so the new note shows before the refetch
      // lands (mirrors AssignReviewersCard's primeDetailCache).
      qc.setQueryData(["ess-detail", fresh.id], fresh);
      invalidateSuggestionCaches(qc, suggestion.id);
      enqueueSnackbar("Note added", { variant: "success" });
      setText("");
      setFiles([]);
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Could not add note"), {
        variant: "error",
      });
    },
  });

  const notes = suggestion.notes ?? [];

  return (
    <Stack spacing={2}>
      {notes.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
          No notes yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {notes.map((n) => (
            <Box
              key={n.id}
              sx={{
                p: "12px 14px",
                // Soft copper wash — same 4% primary tint the status
                // timeline uses for remark callouts, so notes read as
                // the same family.
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                borderRadius: 1,
                borderLeft: "2px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 0.5 }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {n.created_by_name ?? n.created_by ?? "Unknown"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {formatDateTime(n.created_at)}
                </Typography>
              </Stack>
              <Typography
                sx={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.6 }}
              >
                {n.note}
              </Typography>
              {n.attachment_ids && n.attachment_ids.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="flex-end"
                  sx={{ mt: 1 }}
                >
                  {n.attachment_ids.map((aid) => (
                    <AttachmentLink key={aid} assetId={aid} />
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {canAdd.data === true && !closed && (
        // Composer reads as a single input surface: a bordered field that
        // lights up on focus, with the attach + Add Note CTAs docked on a
        // toolbar inside the field. This keeps the write surface visually
        // distinct from the note bubbles above it.
        <Box
          sx={(theme) => ({
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            transition: "border-color .15s ease, box-shadow .15s ease",
            "&:focus-within": {
              borderColor: "primary.main",
              boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          })}
        >
          <TextField
            placeholder="Write a note — visible to everyone who can view this suggestion"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            variant="standard"
            slotProps={{ input: { disableUnderline: true } }}
            sx={{ px: 1.5, pt: 1.25 }}
          />
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{
              px: 1,
              py: 0.75,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <AttachmentUploader
              variant="icon"
              files={files}
              onChange={setFiles}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || text.trim().length === 0}
              sx={{ flexShrink: 0 }}
            >
              {mutation.isPending ? "Adding…" : "Add Note"}
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
